import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import type { BookmarksDisplayMode } from '../admin/settings/useData';

const BOOKMARK_MODES: BookmarksDisplayMode[] = ['sidebar', 'dropdown', 'bar', 'pins', 'none'];

const BOOKMARK_LABELS: Record<BookmarksDisplayMode, string> = {
  sidebar: 'Sidebar',
  dropdown: 'Topbar dropdown',
  bar: 'Quick access bar',
  pins: 'Icon pins',
  none: 'Hidden',
};

const BOOKMARK_DESCRIPTIONS: Record<BookmarksDisplayMode, string> = {
  sidebar: 'Show bookmarks as a group in the left navigation.',
  dropdown: 'Open bookmarks from a star menu in the top bar.',
  bar: 'Keep a horizontal bookmark strip under the top bar.',
  pins: 'Pin bookmark icons next to search in the top bar.',
  none: 'Hide bookmarks from the chrome (manage them elsewhere).',
};

/** One-line confirmation after changing bookmarks placement. */
export const BOOKMARK_MODE_TOAST: Record<BookmarksDisplayMode, string> = {
  sidebar: 'Bookmarks now in the sidebar',
  dropdown: 'Bookmarks now in the top bar (dropdown)',
  bar: 'Bookmarks now in the top bar',
  pins: 'Bookmarks now pinned in the top bar',
  none: 'Bookmarks are hidden from the chrome',
};

function StarDot({ className }: { className?: string }) {
  return <span className={cn('inline-block h-1.5 w-1.5 rounded-full bg-amber-400', className)} />;
}

function BookmarksPreview({ mode }: { mode: BookmarksDisplayMode }) {
  const shell = 'bg-[#0a1424]';
  const panel = 'bg-slate-50 dark:bg-slate-900/40';
  const topbar = 'bg-white dark:bg-slate-800';
  const muted = 'bg-slate-300 dark:bg-slate-600';

  return (
    <div className="relative flex h-28 w-full overflow-hidden rounded-xl border border-black/10 shadow-inner dark:border-white/10">
      {/* Sidebar — highlighted when mode is sidebar */}
      <div className={cn('flex w-[28%] flex-col gap-1 p-2', shell)}>
        <div className="h-1.5 w-full rounded-full bg-[#6b8fb8]/90" />
        <div className="mt-1 h-1 w-4/5 rounded-full bg-white/25" />
        {mode === 'sidebar' ? (
          <div className="mt-1 space-y-1 rounded-md bg-white/10 p-1.5 ring-1 ring-amber-400/50">
            <div className="flex items-center gap-1">
              <StarDot />
              <span className="h-1 flex-1 rounded-full bg-amber-300/80" />
            </div>
            <div className="flex items-center gap-1 pl-0.5">
              <StarDot className="opacity-70" />
              <span className="h-1 w-3/4 rounded-full bg-white/35" />
            </div>
            <div className="flex items-center gap-1 pl-0.5">
              <StarDot className="opacity-50" />
              <span className="h-1 w-2/3 rounded-full bg-white/25" />
            </div>
          </div>
        ) : (
          <>
            <div className="h-1 w-3/5 rounded-full bg-white/15" />
            <div className="h-1 w-4/5 rounded-full bg-white/15" />
          </>
        )}
        <div className="mt-auto h-1.5 w-full rounded-full bg-[#6b8fb8]/70" />
      </div>

      <div className={cn('flex min-w-0 flex-1 flex-col', panel)}>
        {/* Topbar */}
        <div
          className={cn(
            'flex h-6 items-center gap-1.5 border-b border-black/5 px-2 dark:border-white/5',
            topbar,
          )}
        >
          <span className={cn('h-1 w-10 rounded-full opacity-50', muted)} />
          <span className="ml-auto flex items-center gap-1">
            {mode === 'pins' && (
              <>
                <StarDot />
                <StarDot className="opacity-80" />
                <StarDot className="opacity-60" />
              </>
            )}
            {mode === 'dropdown' && (
              <span className="flex h-3.5 w-3.5 items-center justify-center rounded-md bg-amber-400/20 ring-1 ring-amber-400/60">
                <StarDot className="!h-1 !w-1" />
              </span>
            )}
            {mode !== 'pins' && mode !== 'dropdown' && (
              <span className="h-3 w-3 rounded-full bg-black/5 dark:bg-white/10" />
            )}
          </span>
        </div>

        {/* Bar under topbar */}
        {mode === 'bar' && (
          <div className="flex h-5 items-center gap-1 border-b border-amber-400/30 bg-amber-50/80 px-2 dark:bg-amber-950/30">
            <StarDot />
            <span className="h-1 w-8 rounded-full bg-amber-400/70" />
            <StarDot className="opacity-80" />
            <span className="h-1 w-10 rounded-full bg-amber-400/50" />
            <StarDot className="opacity-60" />
            <span className="h-1 w-6 rounded-full bg-amber-400/40" />
          </div>
        )}

        <div className="flex flex-1 flex-col gap-1.5 p-2.5">
          {mode === 'none' ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-1 opacity-40">
              <span className={cn('h-1.5 w-12 rounded-full', muted)} />
              <span className="text-[9px] font-medium uppercase tracking-wide text-slate-400">
                No bookmarks UI
              </span>
            </div>
          ) : (
            <>
              <div className={cn('h-1.5 w-16 rounded-full opacity-60', muted)} />
              <div className="mt-0.5 flex-1 rounded-lg border border-black/5 bg-white/60 p-2 dark:border-white/5 dark:bg-white/5">
                <div className={cn('mb-1.5 h-1 w-3/4 rounded-full opacity-40', muted)} />
                <div className={cn('mb-1 h-1 w-full rounded-full opacity-25', muted)} />
                <div className={cn('h-1 w-2/3 rounded-full opacity-25', muted)} />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export type BookmarksDisplayPickerProps = {
  value: BookmarksDisplayMode;
  onChange: (mode: BookmarksDisplayMode) => void;
  className?: string;
};

/**
 * Visual bookmarks placement picker for Account settings → Workspace.
 */
export function BookmarksDisplayPicker({ value, onChange, className }: BookmarksDisplayPickerProps) {
  const { colors } = useTheme();

  return (
    <div
      role="radiogroup"
      aria-label="Bookmarks placement"
      className={cn('grid grid-cols-1 gap-3 sm:grid-cols-2', className)}
    >
      {BOOKMARK_MODES.map((mode) => {
        const selected = value === mode;
        return (
          <button
            key={mode}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(mode)}
            className={cn(
              'group relative rounded-2xl border-2 p-3 text-left transition-all',
              selected
                ? cn(colors.primaryBorderAll, 'bg-slate-50/90 shadow-sm dark:bg-white/[0.04]')
                : cn(colors.border, 'border', colors.interactiveHover),
            )}
          >
            <BookmarksPreview mode={mode} />
            <div className="mt-3 flex items-start gap-2.5">
              <span
                className={cn(
                  'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-colors',
                  selected
                    ? cn(colors.primary, colors.primaryText, 'border-transparent')
                    : cn(colors.border, 'bg-transparent'),
                )}
                aria-hidden
              >
                {selected && (
                  <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </span>
              <span className="min-w-0">
                <span className={cn('block text-sm font-semibold', colors.text)}>
                  {BOOKMARK_LABELS[mode]}
                </span>
                <span className={cn('mt-0.5 block text-xs leading-snug', colors.textMuted)}>
                  {BOOKMARK_DESCRIPTIONS[mode]}
                </span>
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );
}

export default BookmarksDisplayPicker;
